import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useSurveyRedCertStore } from '../../../../stores/storeRoot';
import {
  AnswerTypeEnum,
  QuestionSection,
  QuestionTypeEnum,
  SurveyQuestionType,
} from './types';
import {
  AddSurveyQuestionRequest,
  SurveyQuestionDto,
} from '../../../../services/cropConnectApiClient';

interface UseSurveyQuestionsProps {
  surveyId: string;
  onQuestionsUpdate?: (hasSavedQuestions: boolean, hasUnsaved: boolean) => void;
}

export const useSurveyQuestions = ({
  surveyId,
  onQuestionsUpdate,
}: UseSurveyQuestionsProps) => {
  const {
    addSurveyQuestion,
    updateSurveyQuestion,
    deleteSurveyQuestion,
    fetchSurvey,
    surveys,
  } = useSurveyRedCertStore();

  const [questionSections, setQuestionSections] = useState<QuestionSection[]>(
    []
  );
  const [loadingQuestions, setLoadingQuestions] = useState<
    Record<string, boolean>
  >({});
  const [selectedQuestionType, setSelectedQuestionType] = useState<
    QuestionTypeEnum | ''
  >('');
  const [selectedAnswerType, setSelectedAnswerType] = useState<
    AnswerTypeEnum | ''
  >('');

  useEffect(() => {
    const survey = surveys.find((s) => s.data?.accessId === surveyId);
    const apiQuestions =
      survey?.data?.questions?.map(surveyQuestionFromApiData) || [];

    setQuestionSections((prev) => {
      const apiAccessIds = new Set(apiQuestions.map((q) => q.status.accessId));
      const localQuestions = prev.filter(
        (q) =>
          !q.status.isSaved ||
          (q.status.accessId && !apiAccessIds.has(q.status.accessId))
      );
      return [...apiQuestions, ...localQuestions];
    });
  }, [surveys, surveyId]);

  const createDefaultQuestion = useCallback(
    () => ({
      id: uuidv4(),
      text: '',
      type: selectedQuestionType || '',
      answerType: selectedAnswerType || '',
      possibleAnswers: undefined,
      prefilledAnswer: undefined,
      hint: { text: '', enabled: false },
      description: '',
      status: {
        isSaved: false,
        isEditable: true,
        accessId: undefined,
        originalData: undefined,
      },
      order: questionSections.length + 1,
    }),
    [selectedQuestionType, selectedAnswerType, questionSections.length]
  );

  const isQuestionValid = useCallback((section: QuestionSection): boolean => {
    const isTextValid = section.text?.trim().length > 0;
    if (!isTextValid || !section.type) return false;

    if (section.type === QuestionTypeEnum.PREFILLED) {
      return true;
    }

    if (section.type === QuestionTypeEnum.STANDARD) {
      if (!section.answerType) return false;

      if (section.answerType === AnswerTypeEnum.YES_NO) {
        return (
          section.possibleAnswers?.every((opt) => opt.trim().length > 0) ??
          false
        );
      }
    }

    if (section.hint.enabled && !section.hint.text?.trim()) {
      return false;
    }

    return true;
  }, []);

  const handleAddQuestion = useCallback(() => {
    const newQuestion = createDefaultQuestion();
    setQuestionSections((prev) => [...prev, newQuestion]);
  }, [createDefaultQuestion]);

  const handleSaveQuestion = useCallback(
    async (questionId: string) => {
      const section = questionSections.find((q) => q.id === questionId);
      if (!section || !isQuestionValid(section)) return;

      const isUpdate = section.status.isSaved;
      setLoadingQuestions((prev) => ({ ...prev, [questionId]: true }));

      try {
        let response;
        if (isUpdate) {
          response = await updateSurveyQuestion(
            surveyId,
            questionId,
            surveyQuestionToApiData(section)
          );
        } else {
          response = await addSurveyQuestion(
            surveyId,
            surveyQuestionToApiData(section)
          );
        }

        if (response?.data) {
          setQuestionSections((prev) =>
            prev.map((q) =>
              q.id === questionId
                ? {
                    ...q,
                    ...response.data,
                    status: {
                      ...q.status,
                      isSaved: true,
                      isEditable: false,
                      originalData: undefined,
                    },
                  }
                : q
            )
          );
          await fetchSurvey(surveyId);
        }
      } catch (error) {
        console.error('Error saving question:', error);
      } finally {
        setLoadingQuestions((prev) => ({ ...prev, [questionId]: false }));
      }
    },
    [
      questionSections,
      isQuestionValid,
      addSurveyQuestion,
      updateSurveyQuestion,
      surveyId,
      fetchSurvey,
    ]
  );

  const toggleQuestionEdit = useCallback((questionId: string) => {
    setQuestionSections((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const wasEditable = q.status.isEditable;
          const newEditable = !wasEditable;

          if (newEditable) {
            const { status, ...originalData } = q;
            return {
              ...q,
              status: {
                ...status,
                isEditable: newEditable,
                originalData,
              },
            };
          } else {
            const { ...restStatus } = q.status;
            return {
              ...q,
              status: {
                ...restStatus,
                isEditable: newEditable,
              },
            };
          }
        }
        return q;
      })
    );
  }, []);

  const updateQuestionSection = useCallback(
    (questionId: string, updates: Partial<QuestionSection>) => {
      setQuestionSections((prev) =>
        prev.map((q) => {
          if (q.id === questionId) {
            return { ...q, ...updates };
          }
          return q;
        })
      );
    },
    []
  );

  useEffect(() => {
    const hasUnsaved = questionSections.some((q) => !q.status.isSaved);
    const hasSavable = questionSections.some(
      (q) => !q.status.isSaved && isQuestionValid(q)
    );
    onQuestionsUpdate?.(!hasUnsaved, hasSavable);
  }, [questionSections, isQuestionValid, onQuestionsUpdate]);

  return {
    questionSections,
    setQuestionSections,
    loadingQuestions,
    selectedQuestionType,
    selectedAnswerType,
    setSelectedQuestionType,
    setSelectedAnswerType,
    handleAddQuestion,
    handleSaveQuestion,
    updateQuestionSection,
    isQuestionValid,
    deleteSurveyQuestion,
    toggleQuestionEdit,
    fetchSurvey,
  };
};

const surveyQuestionToApiData = (
  question: QuestionSection
): AddSurveyQuestionRequest => ({
  properties: {
    text: question.text,
    type: question.type as SurveyQuestionType,
    answerType: question.answerType,
    possibleAnswers: question.possibleAnswers,
    prefilledAnswer: question.prefilledAnswer,
    hint: question.hint,
    description: question.description,
  },
  order: question.order,
});

const surveyQuestionFromApiData = (
  question: SurveyQuestionDto
): QuestionSection => ({
  id: question.accessId,
  text: question.properties.text,
  type: question.properties.type,
  answerType: question.properties.answerType,
  possibleAnswers: question.properties.possibleAnswers,
  prefilledAnswer: question.properties.prefilledAnswer,
  hint: question.properties.hint || { text: '', enabled: false },
  description: question.properties.description || '',
  status: {
    isSaved: true,
    isEditable: false,
    accessId: question.accessId,
    originalData: undefined,
  },
  order: question.order,
});
